import {useCallback, useEffect, useState} from "react";
import {AlertTriangle, Camera, Eye, Package, Save, Star, Trash2} from "lucide-react";
import {deleteAdminProduct, getAdminProduct, listAdminProducts, updateAdminProduct, type AdminProductDetail, type AdminProductRow} from "@zaaaz/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminHtmlEditor, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs, AdminToggleField} from "./ui";

export function ProductsView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("produse");
  const [items, setItems] = useState<AdminProductRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminProductDetail | null>(null);
  const [form, setForm] = useState({nume: "", slug: "", cod: "", activ: 0, pret: 0, discount: 0, descriere: ""});
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminProducts({page, pageSize: 20, search: search || undefined})
      .then((r) => {
        setItems(r.items);
        setTotalPages(r.totalPages);
      })
      .catch((e: Error) => setErr(e.message));
  }, [page, search]);
  useEffect(() => { load(); }, [page]);

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) { setOpenId(null); setDetail(null); }
  }

  async function openItem(id: number) {
    setOpenId(id);
    setModalOpen(true);
    const p = await getAdminProduct(id);
    setDetail(p);
    setForm({nume: p.nume, slug: p.slug, cod: p.cod, activ: p.activ, pret: p.pret, discount: p.discount ?? 0, descriere: p.descriere});
  }

  async function saveProduct(id: number) {
    try {
      await updateAdminProduct(id, form);
      load();
      setDetail(await getAdminProduct(id));
      adminToast.success("Product saved.");
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Could not save product");
    }
  }

  function removeProduct(id: number) {
    confirm("Delete product?", async () => {
      await deleteAdminProduct(id);
      closeModal(false);
      load();
      adminToast.success("Product deleted.");
    });
  }

  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.produse.title}
      description="Manage product catalog"
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Products"}]}
    >
      {confirmDialog}
      <AdminSectionTabs section="produse" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(p) => p.id}
        getTitle={(p) => p.nume}
        getSubtitle={(p) => `${p.cod} — ${p.pret} lei`}
        getMeta={(p) => (
          <>
            {p.categorii.map((c) => <AdminBadge key={c} variant="secondary">{c}</AdminBadge>)}
            {p.activ === 1 ? <AdminBadge variant="danger"><AlertTriangle className="h-3 w-3" /></AdminBadge> : null}
            {p.imaginiSynced === 1 ? <AdminBadge variant="danger"><Camera className="h-3 w-3" /></AdminBadge> : null}
            {(p.discount ?? 0) !== 0 ? <AdminBadge variant="danger"><Star className="h-3 w-3" /></AdminBadge> : null}
            <AdminBadge variant="outline">Stoc: {p.stoc}</AdminBadge>
          </>
        )}
        getSearchText={(p) => adminSearchText(p.id, p.nume, p.cod, p.slug, p.pret, p.discount, p.stoc, p.categorii)}
        selectedId={openId}
        onRowClick={(p) => openItem(p.id)}
        emptyTitle="No products"
        emptyDescription="Adjust filters or check back later."
        searchPlaceholder="Search product, code, description..."
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={() => { setPage(1); load(); }}
        clientFilter={false}
      />
      {detail && typeof openId === "number" ? (
        <AdminEditModal
          open={modalOpen}
          onOpenChange={closeModal}
          title={detail.nume}
          description={(
            <span className="admin-modal__meta">
              <span className="admin-modal__meta-chip admin-modal__meta-chip--muted">{detail.cod}</span>
              <span className="admin-modal__meta-chip">Product #{detail.id}</span>
            </span>
          )}
          icon={Package}
          footer={(
            <AdminFormActions>
              {detail.productUrl ? <Button variant="outline" size="sm" asChild><a href={detail.productUrl} target="_blank" rel="noreferrer"><Eye className="h-4 w-4" /> Vizualizare</a></Button> : null}
              <Button type="button" size="sm" onClick={() => saveProduct(openId)}><Save className="h-4 w-4" /> Save</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => removeProduct(openId)}><Trash2 className="h-4 w-4" /> Delete</Button>
            </AdminFormActions>
          )}
        >
          <div className="admin-detail-panel__form">
            <AdminField label="Name">
              <Input value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} placeholder="Name" />
            </AdminField>
            <AdminField label="Slug">
              <Input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} placeholder="Slug" />
            </AdminField>
            <AdminField label="Code">
              <Input value={form.cod} onChange={(e) => setForm({...form, cod: e.target.value})} placeholder="Code" />
            </AdminField>
            <AdminToggleField
              title="Displayed"
              description="Show this product in the store catalog (activ = 0)."
              checked={form.activ === 0}
              onCheckedChange={(v) => setForm({...form, activ: v ? 0 : 1})}
            />
            <AdminField label="Price">
              <Input type="number" value={form.pret} onChange={(e) => setForm({...form, pret: Number(e.target.value)})} />
            </AdminField>
            <AdminField label="Discount">
              <Input type="number" value={form.discount} onChange={(e) => setForm({...form, discount: Number(e.target.value)})} />
            </AdminField>
            <AdminField label="Description">
              <AdminHtmlEditor value={form.descriere} onChange={(html) => setForm({...form, descriere: html})} minHeight={260} />
            </AdminField>
          </div>
        </AdminEditModal>
      ) : null}
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
